import {formatHex, oklch} from 'culori'

import {STANDARD_SHADES} from '../constants'
import {ThemeNameValidationResult} from '../types/s3Upload'

interface PaletteData {
  values: Record<string, string>
  referencePalette?: string
}

interface RawPalette {
  [key: string]: PaletteData
}

type StandardShadesKey = (typeof STANDARD_SHADES)[number]

interface BasePalette {
  group: string
  shades: Partial<Record<StandardShadesKey, string>>
}

interface SemanticPalette {
  group: string
  referencePalette: string
}

export const getCurrentSemanticPalette = (): SemanticPalette[] => {
  const palette: RawPalette = {}
  const rootStyles = getComputedStyle(document.documentElement)

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = sheet.cssRules || sheet.rules
      for (const rule of Array.from(rules)) {
        if (rule instanceof CSSStyleRule && rule.selectorText === ':root') {
          for (const prop of Array.from(rule.style)) {
            const semanticMatch = prop.match(
              /^--semanticPalette-([a-zA-Z]+)-(c\d{3})$/
            )
            if (semanticMatch) {
              const group = semanticMatch[1]
              const shade = semanticMatch[2]
              const value = rootStyles.getPropertyValue(prop).trim()
              const referenceValue = rule.style.getPropertyValue(prop).trim()
              const referenceMatch = referenceValue.match(
                /^var\(--palette-([a-zA-Z]+)-c\d{3}\)$/
              )

              if (!palette[group]) {
                palette[group] = {values: {}, referencePalette: ''}
              }
              palette[group].values[shade] = value
              if (referenceMatch) {
                palette[group].referencePalette = referenceMatch[1]
              }
            }
          }
        }
      }
    } catch (e) {
      // Skip cross-origin stylesheets
      continue
    }
  }

  return Object.entries(palette).map(([group, data]) => ({
    group,
    referencePalette: data.referencePalette || ''
  }))
}

export const getCurrentBasePalette = (): RawPalette => {
  const palette: RawPalette = {}

  // Go through all stylesheets in the document
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList

    try {
      // May throw if the stylesheet is from another origin
      rules = sheet.cssRules
    } catch {
      continue
    }

    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSStyleRule && rule.selectorText === ':root') {
        const {style} = rule

        for (const name of Array.from(style)) {
          const match = name.match(
            /^--palette-([a-zA-Z]+)-(c\d{3}|black|white|transparent)$/
          )
          if (match) {
            const group = match[1]
            const shade = match[2]
            const value = style.getPropertyValue(name).trim()

            if (!palette[group]) {
              palette[group] = {values: {}}
            }

            palette[group].values[shade] = value
          }
        }
      }
    }
  }

  return palette
}

export const transformPaletteObjectToArray = (
  palette: RawPalette
): BasePalette[] => {
  return Object.entries(palette).map(([group, data]) => ({
    group,
    shades: data.values
  }))
}

/**
 * Generates a palette of shades (c050–c950) from a given C500 color using OKLCH interpolation.
 * @param c500 - The base color (hex string) for c500.
 * @returns Record of shade keys to hex color values.
 */
export function generatePaletteFromC500(c500: string): Record<string, string> {
  // Ratios for interpolation (0 = c500, 1 = endpoint)
  const lighterRatios = [1.0, 0.8, 0.6, 0.4, 0.2] // c050-c400
  const darkerRatios = [0.2, 0.4, 0.6, 0.8, 1.0] // c600-c950

  const Lmax = 0.98 // Lightest
  const Lmin = 0.15 // Darkest

  const c500Oklch = oklch(c500)
  if (!c500Oklch) throw new Error('Invalid C500 color')
  const L500 = c500Oklch.l

  /**
   * We only want to generate shades for the standard shades.
   * The algorithm generates the colors based on the c500 color.
   * It used the rations to generate other shades based on the c500 color.
   */
  return STANDARD_SHADES.reduce(
    (acc, shade, i) => {
      if (shade === 'c500') {
        acc[shade] = c500
      } else {
        let l = L500
        if (i < 5) {
          // lighter shades
          l = L500 + (Lmax - L500) * lighterRatios[i]
        } else if (i > 5) {
          // darker shades
          l = L500 + (Lmin - L500) * darkerRatios[i - 6]
        }
        // Clamp l to [0,1]
        l = Math.max(0, Math.min(1, l))
        const color = formatHex({
          mode: 'oklch',
          l,
          c: c500Oklch.c,
          h: c500Oklch.h
        })
        acc[shade] = color
      }
      return acc
    },
    {} as Record<string, string>
  )
}

/**
 * Applies CSS variables to the DOM
 * @param cssVars - An array of CSS variables to apply to the DOM
 */
export const applyCssVarsToDOM = (
  cssVars: Array<readonly [string, string]>
) => {
  cssVars.forEach(([name, value]) => {
    document.documentElement.style.setProperty(name, value)
  })
}

/**
 * Converts an array of CSS variables to a string
 * @param cssVars - An array of CSS variables to convert to a string
 * @returns A string of CSS variables
 */
export const cssVarsToString = (cssVars: Array<readonly [string, string]>) => {
  return cssVars.map(([name, value]) => `  ${name}: ${value};`).join('\n')
}

/**
 * Creates CSS variables for a color group
 * @param group - The group of the palette (e.g. 'rockBlack', 'amethystPurple), etc
 * @param shades - The shades of the palette (e.g. 'c050', 'c100', etc)
 * @returns An array of CSS variables
 */
export const createColorGroupVariables = ({group, shades}: BasePalette) =>
  Object.entries(shades).map(
    ([shade, color]) => [`--palette-${group}-${shade}`, color] as const
  )

/**
 * Creates CSS variables for base palettes
 * @param basePalettes - An array of base palette colors
 * @returns An array of CSS variables
 */
export const createBasePaletteVariables = (basePalettes: BasePalette[]) =>
  basePalettes.flatMap(createColorGroupVariables)

/**
 * Generates CSS variables string for base palette colors
 * @param basePalettes - An array of base palette colors
 * @returns A string of CSS variables
 */
export function generateBasePaletteCss(basePalettes: BasePalette[]): string {
  const cssVars = createBasePaletteVariables(basePalettes)

  return cssVarsToString(cssVars)
}

/**
 * Creates CSS variables for a semantic palette
 * @param group - The group of the semantic palette (e.g. 'brand', 'primary', etc)
 * @param referencePalette - The reference palette of the semantic palette (e.g. 'wewardOrange', 'wewardBlue', etc)
 * @returns An array of CSS variables
 */
export const createSemanticPaletteVariable = ({
  group,
  referencePalette
}: SemanticPalette) =>
  STANDARD_SHADES.map(
    shade =>
      [
        `--semanticPalette-${group}-${shade}`,
        `var(--palette-${referencePalette}-${shade})`
      ] as const
  )

/**
 * Generates CSS variables for semantic palettes
 * @param semanticPalettes - An array of semantic palette colors
 * @returns An array of CSS variables
 */
export const createSemanticPaletteVariables = (
  semanticPalettes: SemanticPalette[]
) => semanticPalettes.flatMap(createSemanticPaletteVariable)

/**
 * Generates CSS variables string for semantic palette colors
 * @param semanticPalettes - An array of semantic palette colors
 * @returns A string of CSS variables
 */
export function generateSemanticPaletteCss(
  semanticPalettes: SemanticPalette[]
): string {
  const cssVars = createSemanticPaletteVariables(semanticPalettes)

  return cssVarsToString(cssVars)
}

/**
 * Generates complete CSS string with :root selector
 * @param basePalettes - An array of base palette colors
 * @param semanticPalettes - An array of semantic palette colors
 * @returns A string of CSS variables
 */
export function generateCssThemeString(
  basePalettes: BasePalette[],
  semanticPalettes: SemanticPalette[]
): string {
  const baseCssVariables = generateBasePaletteCss(basePalettes)
  const semanticCssVariables = generateSemanticPaletteCss(semanticPalettes)

  return `:root {
${baseCssVariables}
${semanticCssVariables}
}`
}

/**
 * Validates a theme name for S3 upload
 * @param themeName - The theme name to validate
 * @returns Validation result with isValid flag and optional error message
 */
export function validateThemeName(
  themeName: string
): ThemeNameValidationResult {
  const trimmed = themeName.trim()
  if (!trimmed) return {isValid: false, error: 'Theme name is required'}
  if (trimmed.length < 2)
    return {
      isValid: false,
      error: 'Theme name must be at least 2 characters long'
    }

  const validPattern = /^[a-zA-Z0-9_-]+$/
  if (!validPattern.test(trimmed))
    return {
      isValid: false,
      error:
        'Theme name can only contain letters, numbers, hyphens, and underscores'
    }
  return {isValid: true}
}
